import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { Loading } from '../components/ui';
import { LoginPage } from '../features/auth/LoginPage';
import { useAuth } from './AuthProvider';
import { Shell } from './Shell';
import { TenantScope } from './TenantScope';

// Heavy screens (3D, editor, PDF) are loaded on demand.
const TenantsPage = lazy(() => import('../features/tenants/TenantsPage'));
const PlansPage = lazy(() => import('../features/plans/PlansPage'));
const PlanPage = lazy(() => import('../features/plans/PlanPage'));
const AssetsPage = lazy(() => import('../features/assets/AssetsPage'));
const AlertsPage = lazy(() => import('../features/alerts/AlertsPage'));
const AnalyticsPage = lazy(() => import('../features/analytics/AnalyticsPage'));
const UsersPage = lazy(() => import('../features/users/UsersPage'));
const IntegrationPage = lazy(() => import('../features/integration/IntegrationPage'));
const ScanPage = lazy(() => import('../features/scan/ScanPage'));
const PickingPage = lazy(() => import('../features/picking/PickingPage'));
const DashboardPage = lazy(() => import('../features/dashboard/DashboardPage'));
const OverviewPage = lazy(() => import('../features/tenants/OverviewPage'));

const RETURN_TO = 'gonio.returnTo';
const remember = (path: string) => { try { sessionStorage.setItem(RETURN_TO, path); } catch { /* private mode */ } };
const recall = () => { try { const v = sessionStorage.getItem(RETURN_TO); sessionStorage.removeItem(RETURN_TO); return v; } catch { return null; } };

/** Sends anonymous visitors to the login page, then back to the page they asked for (e.g. a scanned QR label). */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user) {
    remember(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function Home() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const back = recall();
  if (back && back.startsWith('/') && !back.startsWith('//')) return <Navigate to={back} replace />;
  return <Navigate to={user.role === 'platform_admin' ? '/admin/overview' : `/t/${user.tenantId}/dashboard`} replace />;
}

/** `/scan?a=<id>`: address printed in equipment QR labels, opened by the phone camera. */
function ScanEntry() {
  const { user } = useAuth();
  const { search } = useLocation();
  if (!user?.tenantId) return <Navigate to="/" replace />;
  return <Navigate to={`/t/${user.tenantId}/scan${search}`} replace />;
}

function AdminLayout() {
  const { user } = useAuth();
  if (user?.role !== 'platform_admin') return <Navigate to="/" replace />;
  return <Shell inTenant={false}><Suspense fallback={<Loading />}><Outlet /></Suspense></Shell>;
}

function TenantLayout() {
  return (
    <TenantScope>
      <Shell inTenant><Suspense fallback={<Loading />}><Outlet /></Suspense></Shell>
    </TenantScope>
  );
}

export function App() {
  const { user, loading } = useAuth();
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={loading ? <Loading /> : user ? <Navigate to="/" replace /> : <LoginPage />} />
        <Route path="/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<OverviewPage />} />
          <Route path="tenants" element={<TenantsPage />} />
        </Route>
        <Route path="/t/:tenantId" element={<RequireAuth><TenantLayout /></RequireAuth>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="plans" element={<PlansPage />} />
          <Route path="plans/:planId" element={<PlanPage />} />
          <Route path="assets" element={<AssetsPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="integration" element={<IntegrationPage />} />
          <Route path="scan" element={<ScanPage />} />
          <Route path="picking" element={<PickingPage />} />
        </Route>
        <Route path="/scan" element={<RequireAuth><ScanEntry /></RequireAuth>} />
        <Route path="*" element={loading ? <Loading /> : <Home />} />
      </Routes>
    </BrowserRouter>
  );
}
